/**
 * Calculate the current billing period key for AI credits.
 *
 * For subscribers with a `subscriptionEnd` date, the period starts on the
 * anchor day (day-of-month from subscriptionEnd) each month.
 *   e.g. subscriptionEnd = 2026-03-15 → anchor day = 15
 *        today = 2026-03-02 → period = "2026-02-15"
 *        today = 2026-03-16 → period = "2026-03-15"
 *
 * For free users (no subscriptionEnd), returns calendar month "YYYY-MM".
 */
export function getBillingPeriod(subscriptionEnd: string | null | undefined): string {
  const now = new Date();

  if (!subscriptionEnd) {
    // Free user: calendar month
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  }

  const endDate = new Date(subscriptionEnd);
  const anchorDay = endDate.getUTCDate();

  // Determine if we've passed the anchor day this month
  const currentDay = now.getDate();
  let periodStart: Date;

  if (currentDay >= anchorDay) {
    // Period started this month
    periodStart = new Date(now.getFullYear(), now.getMonth(), anchorDay);
  } else {
    // Period started last month
    periodStart = new Date(now.getFullYear(), now.getMonth() - 1, anchorDay);
  }

  // Handle months where anchorDay exceeds the month's last day
  // (e.g. anchor 31, but month has 28 days → Date auto-adjusts)
  // We re-clamp to ensure the format is correct
  const y = periodStart.getFullYear();
  const m = String(periodStart.getMonth() + 1).padStart(2, "0");
  const d = String(periodStart.getDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
}
